import type { NavigateFunction } from 'react-router-dom';
import { resolveIndexerPagePath } from './indexer-paths';

const INDEX_PREFILL_KEY = 'if:index-url-prefill';
const INSPECT_PREFILL_KEY = 'if:inspect-url-prefill';

export type UrlListPrefillPayload = {
  urls: string[];
  source?: string;
};

function readKey(key: string): UrlListPrefillPayload | null {
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as UrlListPrefillPayload;
    if (!Array.isArray(parsed.urls) || parsed.urls.length === 0) return null;
    const urls = parsed.urls.filter(
      (item) => typeof item === 'string' && item.trim().length > 0,
    );
    if (urls.length === 0) return null;
    return { urls, source: parsed.source };
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
}

function writeKey(key: string, payload: UrlListPrefillPayload): void {
  sessionStorage.setItem(key, JSON.stringify(payload));
}

function clearKey(key: string): void {
  sessionStorage.removeItem(key);
}

export function stashIndexUrlPrefill(urls: string[], source?: string): void {
  writeKey(INDEX_PREFILL_KEY, { urls, source });
}

export function takeIndexUrlPrefill(): UrlListPrefillPayload | null {
  const payload = readKey(INDEX_PREFILL_KEY);
  if (payload) clearKey(INDEX_PREFILL_KEY);
  return payload;
}

export function peekIndexUrlPrefill(): UrlListPrefillPayload | null {
  return readKey(INDEX_PREFILL_KEY);
}

export function stashInspectUrlPrefill(urls: string[], source?: string): void {
  writeKey(INSPECT_PREFILL_KEY, { urls, source });
}

export function takeInspectUrlPrefill(): UrlListPrefillPayload | null {
  const payload = readKey(INSPECT_PREFILL_KEY);
  if (payload) clearKey(INSPECT_PREFILL_KEY);
  return payload;
}

export function readRouteUrlPrefill(
  state: unknown,
): UrlListPrefillPayload | null {
  if (!state || typeof state !== 'object') return null;
  const prefillUrls = (state as { prefillUrls?: unknown }).prefillUrls;
  if (!Array.isArray(prefillUrls)) return null;
  const urls = prefillUrls.filter(
    (item): item is string => typeof item === 'string' && item.trim().length > 0,
  );
  if (urls.length === 0) return null;
  const source = (state as { prefillSource?: unknown }).prefillSource;
  return {
    urls,
    source: typeof source === 'string' ? source : undefined,
  };
}

export function navigateToIndexWithPrefill(
  navigate: NavigateFunction,
  urls: string[],
  source: string,
  path?: string,
) {
  const target = resolveIndexerPagePath('index', path);
  stashIndexUrlPrefill(urls, source);
  navigate(target, {
    state: { prefillUrls: urls, prefillSource: source },
  });
}

export function navigateToInspectWithPrefill(
  navigate: NavigateFunction,
  urls: string[],
  source: string,
  path?: string,
) {
  const target = resolveIndexerPagePath('url-inspection', path);
  stashInspectUrlPrefill(urls, source);
  navigate(target, {
    state: { prefillUrls: urls, prefillSource: source },
  });
}
